import ModalHeader from '@molecules/ModalHeader';
import ModalWindow from '@organisms/modals/ModalWindow';
import {FC, useEffect, useState} from 'react';
import {
  AttentionIconWrapper,
  AttentionTextWrapper,
  AttentionWrapper,
  ButtonsWrapper,
  ModalContent,
  ModalWrapper,
  SelectedUsersInfoWrapper,
  SelectedUsersRow,
  SelectedUsersTextWrapper,
  SelectedUsersWrapper,
} from './EditAccessRightsModal.styles';
import Button from '@atoms/Button';
import {WarningFilled} from '@carbon/icons-react';
import {theme} from '@styles/theme';
import Text from '@atoms/Text';
import {users, roles} from '@store/slices/users';
import {IRole, IUser} from '@types';
import {useDispatch, useSelector} from 'react-redux';
import MultiSelect from '@atoms/MultiSelect';
import Avatar from '@atoms/Avatar';
import OptionButton from '@atoms/OptionButton';
import {updateSystemUsers} from '@store/slices/systems';
import {AppDispatch} from '@store';
import {toast} from 'react-toastify';
import Toast from '@atoms/Toast';
import {EUserRoles, EUserScopedRoles} from '@constants/usersRoles';

interface Props {
  isOpened: boolean;
  onCloseRequest: () => void;
  systemID: number;
  defaultUsers?: IUser[];
  systemOwnerId?: number;
  currentScopedRole?: number;
  currentRole?: number;
}

const EditAccessRightsModal: FC<Props> = ({
  isOpened,
  onCloseRequest,
  systemID,
  defaultUsers,
  systemOwnerId,
  currentScopedRole,
  currentRole,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [selectedUsersIDs, setSelectedUsersIDs] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<IUser[]>([]);
  const usersArr: IUser[] = useSelector(users);
  const rolesArr: IRole[] = useSelector(roles);
  const adminOptions = usersArr
    .filter(user => user.id !== systemOwnerId)
    .map(user => {
      return {
        title: user.username,
        subtitle: user.email,
        value: String(user.id),
        avatar: user.avatar_url,
      };
    });
  const roleOptions = rolesArr
    .map(role => {
      return {title: role.name, value: String(role.id)};
    })
    .filter(role => {
      if (
        currentRole === EUserRoles.BASE_USER &&
        currentScopedRole !== EUserScopedRoles.ADMIN
      ) {
        return role.value !== String(EUserScopedRoles.ADMIN);
      }
      return true;
    })
    .concat({title: 'Удалить', value: 'delete'});

  useEffect(() => {
    if (defaultUsers?.length) {
      // сначала записываем selectedUsers, чтобы после обновления selectedUsersIDs подхватилась текущая scoped_role_id
      setSelectedUsers(() => {
        return defaultUsers?.map(user => {
          return {
            id: Number(user.id),
            username: user?.username || '',
            email: user?.email || '',
            avatar: user?.avatar_url || '',
            scoped_role_id: user.scoped_role_id || 1, // Берем существующую роль или "1" по умолчанию
          };
        });
      });
      const mapDefaultUsersIDs = defaultUsers?.map(user => {
        return String(user.id);
      });
      setSelectedUsersIDs(mapDefaultUsersIDs || []);
    }
  }, [defaultUsers]);

  useEffect(() => {
    setSelectedUsers(prevUsers => {
      const existingRoles = new Map(
        prevUsers.map(user => [user.id, user.scoped_role_id]),
      );
      return selectedUsersIDs.map(id => {
        const selectedUser = usersArr.find(user => String(user.id) === id);
        return {
          id: Number(id),
          username: selectedUser?.username || '',
          email: selectedUser?.email || '',
          avatar: selectedUser?.avatar_url || '',
          scoped_role_id: existingRoles.get(Number(id)) || 1, // Берем существующую роль или "1" по умолчанию
        };
      });
    });
  }, [selectedUsersIDs]);

  const updateUserRole = (userId: string, newRole: string) => {
    if (newRole === 'delete') {
      setSelectedUsersIDs(prevIDs => prevIDs.filter(id => id !== userId));
    } else {
      setSelectedUsers(prevUsers =>
        prevUsers.map(user =>
          user.id === Number(userId) ? {...user, scoped_role_id: Number(newRole)} : user,
        ),
      );
    }
  };

  const onSave = () => {
    const usersOnUpdate = selectedUsers.map(user => {
      return {
        id: user.id,
        scoped_role_id: user.scoped_role_id || 1,
      };
    });
    dispatch(updateSystemUsers({system_id: systemID, users: usersOnUpdate}))
      .unwrap()
      .then(() => {
        toast.success(
          <Toast
            title="Готово!"
            text="Права доступа успешно обновлены!"
            type="success"
          />,
        );
        onCloseRequest();
      })
      .catch((err: any) => {
        toast.error(
          <Toast
            title="Ошибка"
            text={err?.message || 'Не удалось обновить права доступа'}
            type="error"
          />,
        );
      });
  };

  return (
    <ModalWindow isOpen={isOpened} overflowIsVisible>
      <ModalWrapper>
        <ModalHeader title={'Редактирование прав доступа'} onClose={onCloseRequest} />
        <ModalContent>
          <AttentionWrapper>
            <AttentionIconWrapper>
              <WarningFilled size={22} fill={theme.colors.basic04} />
            </AttentionIconWrapper>
            <AttentionTextWrapper>
              <Text text="Обратите внимание" type="bodyM" color={theme.colors.basic01} />
              <Text
                text="Пользователи системы будут иметь доступ ко всем подсистемам в рамках назначенной роли."
                type="tinyR"
                color={theme.colors.basic02}
              />
            </AttentionTextWrapper>
          </AttentionWrapper>
          <MultiSelect
            id="system-users"
            label="Добавить пользователей"
            value={selectedUsersIDs}
            options={adminOptions}
            onChange={setSelectedUsersIDs}
          />
          {!!selectedUsers.length && (
            <SelectedUsersWrapper>
              {selectedUsersIDs.map(selectedUserID => {
                const selectedUser = selectedUsers.find(
                  user => String(user.id) === selectedUserID,
                );
                return (
                  <SelectedUsersRow key={`edit-access-selected-user-${selectedUserID}`}>
                    <SelectedUsersInfoWrapper>
                      <Avatar img={selectedUser?.avatar_url || ''} />
                      <SelectedUsersTextWrapper>
                        <Text
                          text={selectedUser?.username || ''}
                          type="tinyM"
                          color={theme.colors.basic01}
                        />
                        <Text
                          text={selectedUser?.email || ''}
                          type="tinyR"
                          color={theme.colors.basic02}
                        />
                      </SelectedUsersTextWrapper>
                    </SelectedUsersInfoWrapper>
                    <OptionButton
                      value={String(selectedUser?.scoped_role_id) || '1'}
                      options={roleOptions}
                      fixedWidth={160}
                      withoutIcon
                      onChange={value => updateUserRole(selectedUserID, value)}
                    />
                  </SelectedUsersRow>
                );
              })}
            </SelectedUsersWrapper>
          )}
        </ModalContent>
        <ButtonsWrapper>
          <Button
            onClick={onSave}
            fullWidth
            title="Сохранить"
            variant="filled"
            disabled={!selectedUsersIDs.length}
          />
          <Button
            fullWidth
            title="Закрыть"
            onClick={() => {
              onCloseRequest();
            }}
          />
        </ButtonsWrapper>
      </ModalWrapper>
    </ModalWindow>
  );
};

export default EditAccessRightsModal;
